/**
 * Product image upload service.
 * Uploads product images to Firebase Storage (web). Image URLs are then stored
 * in Firestore with the product document (businesses/{businessId}/products/all).
 * Subscription plans define max images per product (imagesPerProduct).
 */

import { Platform } from 'react-native';
import { getWebStorage, initializeFirebase } from './unifiedAuthService';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const RECOMMENDED_IMAGE_WIDTH = 800;
const RECOMMENDED_IMAGE_HEIGHT = 800;
const RECOMMENDED_FILE_SIZE_KB = 300;
const ACCEPTED_FORMATS_LABEL = 'JPEG, PNG, WebP, or GIF';

// Bound uploads to the recommended display size so we never ship a full-res
// camera photo for what renders as a small thumbnail in the widget.
const MAX_DIMENSION = Math.max(RECOMMENDED_IMAGE_WIDTH, RECOMMENDED_IMAGE_HEIGHT);
// Separate small derivative for list/thumbnail spots (widget cards, option
// sheets) which render at ~40-48px — avoids shipping the 800px image there.
const THUMBNAIL_DIMENSION = 200;
const JPEG_QUALITY = 0.82;
// Long-lived cache header: uploaded objects are never mutated in place (each
// upload gets a new timestamped path), so it's safe for browsers/CDN to cache
// them indefinitely instead of re-fetching on every page/widget load.
const CACHE_CONTROL = 'public, max-age=31536000, immutable';

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
}

function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image file.'));
    };
    img.src = url;
  });
}

function createScaledCanvas(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  maxDimension: number,
  fillWhite: boolean
): HTMLCanvasElement {
  const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
  const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable.');
  }
  if (fillWhite) {
    // Flatten any transparency onto white before JPEG encoding.
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, targetWidth, targetHeight);
  }
  ctx.drawImage(source, 0, 0, targetWidth, targetHeight);
  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement, isPng: boolean): Promise<Blob | null> {
  const outputType = isPng ? 'image/png' : 'image/jpeg';
  return new Promise((resolve) => canvas.toBlob(resolve, outputType, isPng ? undefined : JPEG_QUALITY));
}

/**
 * Produce both the main (MAX_DIMENSION) and thumbnail (THUMBNAIL_DIMENSION)
 * derivatives, re-encoded as compressed JPEG (or PNG, to preserve
 * transparency). The thumbnail is drawn from the already-downscaled main
 * canvas rather than the native-resolution source a second time — reading a
 * full-resolution phone photo (often 12MP+) twice is the expensive part,
 * not the file size, so this halves the CPU cost per upload.
 */
async function resizeAndCompressImage(
  file: File,
  img: HTMLImageElement
): Promise<{
  main: { blob: Blob; extension: string };
  thumb: { blob: Blob; extension: string };
}> {
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  const isPng = file.type === 'image/png';
  const fallbackExtension = (file.name.split('.').pop() || 'jpg').toLowerCase();

  const mainCanvas = createScaledCanvas(img, width, height, MAX_DIMENSION, !isPng);
  const thumbCanvas = createScaledCanvas(
    mainCanvas,
    mainCanvas.width,
    mainCanvas.height,
    THUMBNAIL_DIMENSION,
    !isPng
  );

  const [mainBlob, thumbBlob] = await Promise.all([
    canvasToBlob(mainCanvas, isPng),
    canvasToBlob(thumbCanvas, isPng),
  ]);

  const extension = isPng ? 'png' : 'jpg';
  return {
    main: mainBlob ? { blob: mainBlob, extension } : { blob: file, extension: fallbackExtension },
    thumb: thumbBlob ? { blob: thumbBlob, extension } : { blob: file, extension: fallbackExtension },
  };
}

async function uploadDerivative(
  storage: any,
  businessId: string,
  folder: string,
  segment: string,
  baseName: string,
  suffix: string,
  timestamp: number,
  blob: Blob,
  extension: string,
  fallbackContentType: string
): Promise<string> {
  const { ref, uploadBytes, getDownloadURL } = require('firebase/storage');
  const path = `businesses/${businessId}/${folder}/${segment}/${timestamp}_${baseName}${suffix}.${extension}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, {
    contentType: (blob as any).type || fallbackContentType,
    cacheControl: CACHE_CONTROL,
  });
  return getDownloadURL(storageRef);
}

/**
 * Shared upload path for both product and service images: validates, resizes
 * to MAX_DIMENSION (+ a THUMBNAIL_DIMENSION derivative), and uploads both to
 * Firebase Storage (web only).
 * Path: businesses/{businessId}/{folder}/{entityId}/{timestamp}_{filename}[_thumb].{ext}
 * @returns Download URLs to store in entity.images[] / entity.imageThumbnails[]
 */
async function uploadEntityImage(
  businessId: string,
  file: File,
  folder: string,
  entityId?: string
): Promise<{ url: string; thumbnailUrl: string }> {
  if (Platform.OS !== 'web') {
    throw new Error('Image upload is available on web. On mobile, add an image URL instead.');
  }
  if (!file || !(file instanceof File)) {
    throw new Error('Invalid file');
  }
  if (!ACCEPTED_TYPES.includes(file.type)) {
    throw new Error('Invalid file type. Use JPEG, PNG, WebP, or GIF.');
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error('Image must be 5 MB or smaller.');
  }

  initializeFirebase();
  const storage = getWebStorage();
  if (!storage) {
    throw new Error('Storage not available. Check Firebase Storage is enabled and storageBucket is set.');
  }

  let mainBlob: Blob = file;
  let mainExtension = (file.name.split('.').pop() || 'jpg').toLowerCase();
  let thumbBlob: Blob = file;
  let thumbExtension = mainExtension;

  if (file.type !== 'image/gif') {
    try {
      const img = await loadImageElement(file);
      const { main, thumb } = await resizeAndCompressImage(file, img);
      mainBlob = main.blob;
      mainExtension = main.extension;
      thumbBlob = thumb.blob;
      thumbExtension = thumb.extension;
    } catch {
      // Fall back to the original file for both derivatives if resize/compress fails.
      mainBlob = file;
      thumbBlob = file;
    }
  }

  const segment = entityId || 'draft';
  const baseName = sanitizeFileName(file.name || 'image').replace(/\.[^.]+$/, '');
  const timestamp = Date.now();

  const [url, thumbnailUrl] = await Promise.all([
    uploadDerivative(storage, businessId, folder, segment, baseName, '', timestamp, mainBlob, mainExtension, file.type),
    uploadDerivative(storage, businessId, folder, segment, baseName, '_thumb', timestamp, thumbBlob, thumbExtension, file.type),
  ]);

  return { url, thumbnailUrl };
}

/**
 * Upload a product image to Firebase Storage (web only), plus a small
 * thumbnail derivative for list/card rendering.
 * @returns Download URLs to store in product.images[] / product.imageThumbnails[]
 */
export async function uploadProductImage(
  businessId: string,
  file: File,
  productId?: string
): Promise<{ url: string; thumbnailUrl: string }> {
  return uploadEntityImage(businessId, file, 'product-images', productId);
}

/**
 * Upload a service image to Firebase Storage (web only), plus a small
 * thumbnail derivative for list/card rendering.
 * @returns Download URLs to store in service.images[] / service.imageThumbnails[]
 */
export async function uploadServiceImage(
  businessId: string,
  file: File,
  serviceId?: string
): Promise<{ url: string; thumbnailUrl: string }> {
  return uploadEntityImage(businessId, file, 'service-images', serviceId);
}

export {
  ACCEPTED_TYPES,
  ACCEPTED_FORMATS_LABEL,
  MAX_FILE_SIZE_BYTES,
  RECOMMENDED_IMAGE_WIDTH,
  RECOMMENDED_IMAGE_HEIGHT,
  RECOMMENDED_FILE_SIZE_KB,
};
